import React, { ReactElement } from "react";
import { Typography, makeStyles, ListItem, List, Dialog, AppBar, IconButton } from "@material-ui/core";
import { MessageCard } from "../../components/MessageCard";
import { FileSetDetailResponse, InvoiceFileResponse } from "../../models/Invoice";
import { InvoiceFileSetDisplayCard } from "./InvoiceFileSetDisplayCard";
import CloseIcon from "@material-ui/icons/Close";
import { AxiosResponse } from "axios";

export interface InvoiceFileSetListContainerProps {
	//The invoice set list to be displayed in the list.
	fileSetList: FileSetDetailResponse[];
	//The callback method for handling reprocessing of the invoice.
	handleReprocessing: (invoiceHistoryId: string) => void;
	//The callback method for handling the submission of the file set to accounting.
	handleFileSubmission: (invoiceHistoryId: string, accountingFileName: string) => void;
	//The callback method for retrieving the invoice or accounting file to be displayed in the dialog.
	retrieveFile: (invoiceHistoryId: string, fileName: string) => Promise<AxiosResponse<InvoiceFileResponse>>;
	//The callback method for updating the invoices note.
	updateNote: (invoiceHistoryId: string, newNote: string) => void;
	//The callback method for generating a download of one of the files.
	generateFileDownloadLink: (invoiceHistoryId: string, fileName: string) => string;
}

const useStyles = makeStyles((theme) => ({
	list: {
		maxWidth: theme.breakpoints.values.md,
	},
	appBar: {
		position: "relative",
	},
	appBarTitle: {
		marginTop: "auto",
		marginBottom: "auto",
	},
	dialogContent: {
		maxWidth: "100%",
		overflow: "auto",
	},
}));

/**
 * This component is used for displaying the invoice history list as well as
 * serving as a place for the invoice dialog.
 * @param props
 */
export const InvoiceFileSetListContainer = (props: InvoiceFileSetListContainerProps) => {
	const [dialogContents, setDialogContents] = React.useState<ReactElement | undefined>(undefined);
	const [titleBarContent, setTitleBarContent] = React.useState<ReactElement | undefined>(undefined);
	const [dialogTitle, setDialogTitle] = React.useState("");
	const classes = useStyles();

	const handleDialogUpdate = (title: string, contents: ReactElement | undefined, titleBarContent?: ReactElement) => {
		setDialogContents(contents);
		setTitleBarContent(titleBarContent);
		setDialogTitle(title);
	};
	return (
		<div>
			<div data-testid="invoice-file-set-list">
				{props.fileSetList && props.fileSetList.length > 0 ? (
					<div>
						<List className={classes.list}>
							{props.fileSetList.map((invoiceSet) => (
								<ListItem key={invoiceSet.invoiceHistoryId} data-testid="invoice-list-item">
									<InvoiceFileSetDisplayCard
										setDialogContents={handleDialogUpdate}
										handleReprocessing={props.handleReprocessing}
										retrieveFile={props.retrieveFile}
										generateFileDownloadLink={props.generateFileDownloadLink}
										updateNote={props.updateNote}
										handleFileSubmission={props.handleFileSubmission}
										invoiceSet={invoiceSet}
									></InvoiceFileSetDisplayCard>
								</ListItem>
							))}
						</List>
					</div>
				) : (
					<MessageCard message="No Invoices found." textAlign="left" />
				)}
			</div>
			<Dialog
				onClose={() => {
					setDialogContents(undefined);
				}}
				open={dialogContents !== undefined}
				maxWidth={"xl"}
			>
				<div>
					<AppBar color="default" className={classes.appBar}>
						<div style={{ display: "flex", justifyContent: "space-between" }}>
							<IconButton
								edge="start"
								color="inherit"
								onClick={() => {
									setDialogContents(undefined);
									setTitleBarContent(undefined);
								}}
								aria-label="close"
								style={{ marginLeft: 5 }}
							>
								<CloseIcon />
							</IconButton>
							<Typography variant="h6" className={classes.appBarTitle}>
								{dialogTitle}
							</Typography>
							<div className={classes.appBarTitle}>{titleBarContent}</div>
						</div>
					</AppBar>
				</div>
				<div className={classes.dialogContent}>{dialogContents}</div>
			</Dialog>
		</div>
	);
};
